import { useState } from 'react';
import PageLayout from '../../../components/PageLayout';
import {
  DatePicker,
  DatePickerInput,
  Modal,
  Pagination,
  Select,
  SelectItem,
} from 'carbon-components-react';
import { useDebounceState } from '../../../utils/useDebounceState';
import useSWR from 'swr';
import {
  formatCurrency,
  formatDate,
  getCurrentUser,
  getItemById,
  getQueryString,
  getStatusColor,
} from '../../../utils/common.util';
import { useUrlState } from '../../../utils/useUrlState';
import { useCustomMutation } from '../../../utils/useCustomMutation';

import { useClassAccessList } from '../../../api/class.service';
import Combobox from '../../../components/combobox/Combobox';
import { StatsBar, StatsTitle } from '../../../components/PrintOutHead';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { Printer } from 'react-feather';
import { RenderIf } from '../../../components/RenderIf';
import ReceivePaymentModal from '../../../components/modal/receive-payment/receive-payment.component';
import { postMethod } from '../../../api/common';
import ReceiptModal from '../../../components/modal/receipt/receipt.component';
import ComponentGuard from '../../../components/component-guard/ComponentGuard';
import { REVENUE_SUB_ROLES } from '../../../utils/user-roles';
import PageGuard from '../../../components/page-guard/PageGuard';

const initialData = {
  dateFrom: '',
  dateTo: '',
  studentId: '',
  classId: '',
  status: 'Completed',
  applied: '',
  take: 20,
  skip: 0,
};

export default function OnlinePaymentsPage() {
  const [query, setQuery] = useUrlState(initialData);

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );
  const student = getItemById(students, query.studentId);

  const { classAccessList: classes } = useClassAccessList();

  const [paginate, setPaginate] = useState({
    page: 1,
    pageSize: 20,
  });
  const {
    data: onlinePaymentsData,
    isLoading,
    error,
    mutate,
  } = useSWR(
    `school-fees-payments?${getQueryString({
      ...query,
      take: paginate.pageSize,
      skip: paginate.page - 1,
    })}`,
  );
  const onlinePayments = onlinePaymentsData?.data || [];

  const [isOpen, setIsOpen] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);

  const { trigger } = useCustomMutation(
    `/school-fees-payments/${selectedPayment?.id}/set-applied-status`,
    (url, { arg }) => postMethod(url, arg),
  );

  return (
    <PageGuard allowedRoles={[REVENUE_SUB_ROLES.ONLINE_REVENUE_APPLICATION]}>
      <PageLayout
        breadcrumbs={[
          { label: 'Cash Received', to: '/payments-received' },
          { label: `Online Receipts`, active: true },
        ]}
        title="Online Receipts"
        isLoading={isLoading}
      >
        {/* FILTERS  */}
        {/* <p className="mt-20 text-12">
            Select at least a student, a fee type, and start date filters to show
            report.
          </p> */}
        <div className="mt-10 card-row-3">
          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="start-date-picker"
            maxDate={new Date().toISOString().split('T')[0]}
            onChange={(value) => {
              if (value.length > 0) {
                setQuery({
                  dateFrom: [
                    value[0].getFullYear(),
                    ('0' + (value[0].getMonth() + 1)).slice(-2),
                    ('0' + value[0].getDate()).slice(-2),
                  ].join('-'),
                });
              } else setQuery({ dateFrom: '' });
            }}
            onClose={(value) => {}}
          >
            <DatePickerInput
              className="start-date"
              id="start-date"
              name="dateFrom"
              labelText="Start Date"
              value={query.dateFrom}
              max={new Date().toISOString().split('T')[0]}
              // pattern="d{1,2}/d{4}"
              pattern="\d{4}-\d{2}-\d{2}"
              autoComplete="off"
              placeholder="YYYY-MM-DD"
              type="text"
              onChange={(e) => setQuery({ dateFrom: e.target.value })}
            />
          </DatePicker>
          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="end-date-picker"
            // minDate={
            //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
            // }
            maxDate={new Date().toISOString().split('T')[0]}
            onChange={(value) => {
              if (value.length > 0) {
                setQuery({
                  dateTo: [
                    value[0].getFullYear(),
                    ('0' + (value[0].getMonth() + 1)).slice(-2),
                    ('0' + value[0].getDate()).slice(-2),
                  ].join('-'),
                });
              } else setQuery({ dateTo: '' });
            }}
            onClose={(value) => {}}
          >
            <DatePickerInput
              className="end-date"
              id="end-date"
              name="dateTo"
              labelText="End Date"
              value={query.dateTo}
              max={new Date().toISOString().split('T')[0]}
              // pattern="d{1,2}/d{4}"
              pattern="\d{4}-\d{2}-\d{2}"
              autoComplete="off"
              placeholder="YYYY-MM-DD"
              type="text"
              onChange={(e) => setQuery({ dateTo: e.target.value })}
            />
          </DatePicker>
          <Select
            labelText="Class"
            onChange={(e) => setQuery({ classId: e.target.value })}
            value={query.classId}
          >
            <SelectItem selected disabled text="Select Class" value="" />
            {classes?.map((item) => (
              <SelectItem text={item.description} value={item.id} />
            ))}
          </Select>
          <Combobox
            options={students ?? []}
            label="Select a Student"
            value={
              query.studentId
                ? students?.find((s) => s.id === Number(query.studentId))
                : null
            }
            onChange={(option) => {
              setQuery({ studentId: option ? option.id : '' });
              setSearchStudent('');
            }}
            onSearch={setSearchStudent} // The combobox now just updates the search term state
            onClear={() => {
              setQuery({ studentId: '' });
              setSearchStudent('');
            }}
            loading={isStudentsLoading} // Pass the loading state down
            renderSelected={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            renderOption={(option) => `${option.firstName} ${option.lastName}`}
            labelKey="fullName"
            valueKey="id"
            searchPlaceholder="Search students..."
            placeholder=""
          />
          <Select
            labelText="Status"
            onChange={(e) => setQuery({ status: e.target.value })}
            value={query.status}
          >
            <SelectItem selected disabled text="Status" value="" />
            <SelectItem text="Completed" value="Completed" />
            <SelectItem text="Pending" value="Pending" />
          </Select>
          <Select
            labelText="Applied"
            onChange={(e) => setQuery({ applied: e.target.value })}
            value={query.applied}
          >
            <SelectItem selected text="All" value="" />
            <SelectItem text="Applied" value="true" />
            <SelectItem text="Unapplied" value="false" />
          </Select>
        </div>

        <div className="mt-20">
          {error && (
            <p className="text-14 mt-10 text-danger">{error.message}</p>
          )}
          <RenderIf condition={onlinePayments?.length > 0}>
            <MainContent
              onlinePaymentsData={onlinePaymentsData}
              setIsOpen={setIsOpen}
              setSelectedPayment={setSelectedPayment}
              paginate={paginate}
              setPaginate={setPaginate}
            />
          </RenderIf>
          {onlinePayments?.length === 0 && (
            <p className="text-14 mt-30 text-grey">No data available.</p>
          )}

          <RenderIf condition={onlinePayments?.length > 0}>
            <div
              className="mt-30"
              style={{
                display: 'flex',
                justifySelf: 'end',
                gap: '20px',
                alignItems: 'center',
              }}
            >
              <CustomButton
                classes="secondary"
                onClick={() => setIsOpen('print')}
              >
                <Printer size="16" strokeWidth="2px" /> &nbsp; Print
              </CustomButton>
            </div>
          </RenderIf>
        </div>

        <RenderIf condition={isOpen === 'receive-payment'}>
          <ReceivePaymentModal
            openModal={isOpen === 'receive-payment'}
            paymentToRevise={null}
            student={selectedPayment?.student}
            showReceipt={(payment) => {
              trigger();
              setSelectedPayment(payment);
              setIsOpen('receipt');
              mutate();
            }}
            cancelAction={() => setIsOpen('')}
            onlinePayment={selectedPayment}
          />
        </RenderIf>
      </PageLayout>

      <RenderIf condition={isOpen === 'receipt'}>
        <ReceiptModal
          openModal={isOpen === 'receipt'}
          selectedPayment={selectedPayment}
          cancelAction={() => setIsOpen('')}
        />
      </RenderIf>

      <RenderIf condition={isOpen === 'print'}>
        <Modal
          id="online-modal"
          className="print-modal big-modal"
          passiveModal={true}
          open={isOpen === 'print'}
          primaryButtonDisabled={false}
        >
          <div className="mr-20">
            <PrintContent
              onlinePaymentsData={onlinePaymentsData}
              student={student}
              classes={classes}
              query={query}
              setIsOpen={setIsOpen}
              setSelectedPayment={setSelectedPayment}
              paginate={paginate}
              setPaginate={setPaginate}
            />
            <div className="buttons-footer">
              <div className="">
                <CustomButton
                  classes="outline secondary f-width font-bold mt-10"
                  onClick={() => setIsOpen('')}
                >
                  CLOSE
                </CustomButton>
              </div>
              <div className="">
                <CustomButton
                  classes="primary f-width font-bold mt-10"
                  onClick={() => window.print()}
                >
                  <Printer size="11" strokeWidth="5px" /> &nbsp; PRINT
                </CustomButton>
              </div>
            </div>
          </div>
        </Modal>
      </RenderIf>
    </PageGuard>
  );
}

/**
 * PAGE STRUCTURE RATIONALE:
 *
 * The component, PrintView, works best when rendering a single, isolated data structure.
 * When rendering multiple unrelated data types on the same page (e.g., a payment table AND
 * a receipt), they cause visual conflicts—overlapping, misaligned spacing, conflicting styles.
 *
 * SOLUTION:
 * the two or more types of data are each rendered in their own modals outside of the page(PageLayout) when ready to print.
 *
 * In this page, `ReceiptModal` and the main page content(online payment table) are the two different types of data that will be printed. `MainContent` and `PrintContent` components exists to facilitate this solution. PrintContent is a wrapper around the MainContent for printing.
 */

function MainContent({
  onlinePaymentsData,
  setIsOpen,
  setSelectedPayment,
  paginate,
  setPaginate,
}) {
  return (
    <div style={{ overflow: 'auto' }}>
      <table className="bx--data-table table-white">
        <thead>
          <tr>
            <th>Date</th>
            <th>Student</th>
            <th>Class</th>
            <th>Description</th>
            <th>
              Amount Paid <br /> (GHS)
            </th>
            <th>
              Applied Amount <br /> (GHS)
            </th>
            <th>
              Unapplied Amount <br /> (GHS)
            </th>
            <th>Paid by</th>
            <th>Status</th>
            <ComponentGuard
              allowedRoles={[REVENUE_SUB_ROLES.ONLINE_REVENUE_APPLICATION]}
            >
              <th className="no-print">Action</th>
            </ComponentGuard>
          </tr>
        </thead>
        <tbody>
          {onlinePaymentsData?.data?.map((payment) => (
            <tr key={payment.id}>
              <td>{formatDate(payment.paymentDate)}</td>
              <td>
                {payment.student.firstName} {payment.student.lastName}
              </td>
              <td>{payment.class.description}</td>
              <td>{payment.description}</td>
              <td>{payment.amount.toFixed(2)}</td>
              <td>{payment.appliedAmount?.toFixed(2)}</td>
              <td>{payment.unappliedAmount?.toFixed(2)}</td>
              <td>{payment.paidBy}</td>
              <td className={getStatusColor(payment.status)}>
                {payment.status}
              </td>
              <ComponentGuard
                allowedRoles={[REVENUE_SUB_ROLES.ONLINE_REVENUE_APPLICATION]}
              >
                <td className="no-print">
                  <RenderIf
                    condition={
                      payment.unappliedAmount > 0 &&
                      payment.status === 'Completed'
                    }
                  >
                    <button
                      className="clean-btn text-primary"
                      onClick={() => {
                        setIsOpen('receive-payment');
                        setSelectedPayment(payment);
                      }}
                    >
                      Apply
                    </button>
                  </RenderIf>
                  <RenderIf
                    condition={
                      payment.unappliedAmount === 0 ||
                      payment.status !== 'Completed'
                    }
                  >
                    <span className="text-grey">N/A</span>
                  </RenderIf>
                </td>
              </ComponentGuard>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td colSpan="4">Total</td>
            <td>
              {formatCurrency(onlinePaymentsData?.extendedData?.totalAmount)}
            </td>
            <td>
              {formatCurrency(
                onlinePaymentsData?.extendedData?.totalAppliedAmount,
              )}
            </td>
            <td>
              {formatCurrency(
                onlinePaymentsData?.extendedData?.totalUnappliedAmount,
              )}
            </td>
            <td colSpan="3"></td>
          </tr>
        </tfoot>
      </table>
      <div className="no-print">
        <Pagination
          backwardText="Previous page"
          forwardText="Next page"
          page={paginate.page}
          pageNumberText="Page Number"
          pageSize={paginate.pageSize}
          pageSizes={[10, 20, 30, 40, 50, 100]}
          totalItems={onlinePaymentsData?.pagingInfo?.recordsCount || 0}
          onChange={(value) => setPaginate(value)}
        />
      </div>
    </div>
  );
}

function PrintContent({
  onlinePaymentsData,
  student,
  classes,
  query,
  setIsOpen,
  setSelectedPayment,
  paginate,
  setPaginate,
}) {
  const { firstName, lastName } = getCurrentUser();

  return (
    <>
      <StatsTitle title={`Online Payment Report`} />
      <hr className="grey-hr mt-210 mb-10" />
      <StatsBar
        statsItems={[
          {
            label: 'Student',
            value: (student?.firstName ?? '') + ' ' + (student?.lastName ?? ''),
          },
          {
            label: 'Class',
            value:
              getItemById(classes, query.classId)?.description || 'All Classes',
          },
          {
            label: 'Date Range',
            value: query.dateFrom
              ? `${formatDate(query.dateFrom)} - ${formatDate(query.dateTo)} `
              : '-',
          },
          { label: 'Date Generated', value: formatDate(new Date()) },
          { label: 'Generated By', value: firstName + ' ' + lastName },
        ]}
      />

      <div className="mt-20" />

      <MainContent
        onlinePaymentsData={onlinePaymentsData}
        setIsOpen={setIsOpen}
        setSelectedPayment={setSelectedPayment}
        paginate={paginate}
        setPaginate={setPaginate}
      />
    </>
  );
}
